import type { ComponentPropsWithoutRef, ComponentType, ReactNode } from 'react'
import { DropdownMenu as Primitive } from 'radix-ui'

import { cn } from '@/lib/cn'

export const DropdownMenu = Primitive.Root
export const DropdownMenuTrigger = Primitive.Trigger

export interface DropdownMenuContentProps
  extends ComponentPropsWithoutRef<typeof Primitive.Content> {
  children: ReactNode
}

export function DropdownMenuContent({
  className,
  align = 'end',
  sideOffset = 6,
  collisionPadding = 8,
  children,
  ...props
}: DropdownMenuContentProps) {
  return (
    <Primitive.Portal>
      <Primitive.Content
        align={align}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        className={cn(
          'z-50 min-w-48 overflow-hidden rounded-xl border border-border bg-surface-raised p-1 shadow-xl',
          className,
        )}
        {...props}
      >
        {children}
      </Primitive.Content>
    </Primitive.Portal>
  )
}

export interface DropdownMenuItemProps
  extends ComponentPropsWithoutRef<typeof Primitive.Item> {
  /** Passed as a component, not an element, so the menu controls icon sizing. */
  icon?: ComponentType<{ className?: string }>
  destructive?: boolean
  /** Right-aligned hint, e.g. a shortcut or a count. */
  trailing?: ReactNode
}

export function DropdownMenuItem({
  icon: Icon,
  destructive = false,
  trailing,
  className,
  children,
  ...props
}: DropdownMenuItemProps) {
  return (
    <Primitive.Item
      className={cn(
        'flex h-11 cursor-pointer select-none items-center gap-3 rounded-lg px-3 text-sm outline-none',
        'data-[highlighted]:bg-surface-hover',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        destructive ? 'text-danger' : 'text-text',
        className,
      )}
      {...props}
    >
      {Icon ? <Icon className="h-4 w-4 shrink-0" aria-hidden="true" /> : null}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {trailing ? <span className="text-xs text-text-subtle">{trailing}</span> : null}
    </Primitive.Item>
  )
}

export function DropdownMenuSeparator({ className }: { className?: string }) {
  return <Primitive.Separator className={cn('my-1 h-px bg-border', className)} />
}

export function DropdownMenuLabel({
  className,
  children,
}: {
  className?: string
  children: ReactNode
}) {
  return (
    <Primitive.Label
      className={cn('px-3 py-2 text-xs font-medium uppercase tracking-wide text-text-subtle', className)}
    >
      {children}
    </Primitive.Label>
  )
}
